import React, { useEffect } from "react";
import { AvatarChat } from "../avatar-chat";
import classNames from "classnames";
import { LikeColorIcon } from "@/icons/color-icons-new";
import { DislikeColorIcon } from "@/icons/color-icons-new";
import { OkIcon } from "@/icons/primary-icons-new";
import { CancelIcon } from "@/icons/primary-icons-new";

const BASE_INPUT_CLASSES = [
  "text-sans-serif",
  "p-0.5",
  "overflow-hidden",
  "rounded-base",
  "w-full",
];

const INTENT_CLASSES = {
  light: {
    base: ["bg-gradient-bright rounded-base"],
    inside: ["border-transparent rounded-base"],
  },
  gradient: { base: ["rounded-xl"], inside: ["bg-gradient-light rounded-xl"] },
};

export type ChatAnswerProps = {
  intent?: "light" | "gradient";
  pictureUrl?: string;
  firstName: string;
  text: string;
  date?: string;
  time?: string;
  editable?: boolean;
  activeEdit?: boolean;
  touchLike?: () => void;
  saveEdit?: (value: string) => Promise<boolean>;
  toggleEdit?: () => void;
};

export function ChatAnswer({
  intent = "light",
  pictureUrl,
  firstName,
  text,
  date,
  time,
  editable = false,
  activeEdit = false,
  touchLike,
  saveEdit,
  toggleEdit,
}: ChatAnswerProps) {
  const [preparedValue, setPreparedValue] = React.useState(text);
  const [heightElement, setHeightElement] = React.useState(0);
  const elementRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (elementRef.current) {
      setHeightElement(elementRef.current.clientHeight);
    }
  }, [elementRef.current]);

  useEffect(() => {
    setPreparedValue(text);
  }, [text]);

  return (
    <div className='w-full'>
      <div
        className={classNames(BASE_INPUT_CLASSES, INTENT_CLASSES[intent].base)}
        style={{
          ...(intent === "gradient"
            ? {
                borderBottomLeftRadius: 0,
              }
            : {}),
        }}
      >
        <div
          className={classNames([
            "flex p-5 w-full ",
            INTENT_CLASSES[intent].inside,
            intent === "light" && activeEdit ? "bg-deep-blue text-white" : "bg-white",
          ])}
          style={{
            ...(intent === "gradient"
              ? {
                  borderBottomLeftRadius: 0,
                }
              : {}),
          }}
        >
          {!activeEdit ? (
            <div className='flex' ref={elementRef}>
              <AvatarChat width={40} pictureUrl={pictureUrl} />
              <div className={`flex-col ml-5 w-full`}>
                <div className='flex font-normal justify-between'>
                  <div className={`flex items-center `}>
                    <div
                      className={`font-semibold ${intent === "gradient" ? "text-xs" : "text-sm"}`}
                    >
                      Digital {firstName}
                    </div>
                    {date && <div className='ml-2.5 text-xs text-mid-neutral-1'>{date}</div>}
                  </div>
                  {time && <small className='ml-2.5'>{time}</small>}
                </div>
                {text && (
                  <div
                    className='mt-2 text-sm whitespace-pre-line'
                    dangerouslySetInnerHTML={{ __html: text }}
                  />
                )}
              </div>
            </div>
          ) : (
            <textarea
              style={{ height: heightElement }}
              className='bg-transparent text-sm w-full'
              value={preparedValue}
              onChange={(e) => setPreparedValue(e.target.value)}
            />
          )}
        </div>
      </div>
      {editable && (
        <>
          {activeEdit ? (
            <div className='flex justify-end gap-2.5 mt-5'>
              <OkIcon
                className='cursor-pointer'
                onClick={async () => {
                  if (saveEdit) {
                    const result = await saveEdit(preparedValue);

                    if (toggleEdit && result) toggleEdit();
                  }
                }}
                style={{ width: "26px" }}
              />
              <CancelIcon
                onClick={() => {
                  if (toggleEdit) toggleEdit();
                  setPreparedValue(text);
                }}
                className='cursor-pointer'
                style={{ width: "26px" }}
              />
            </div>
          ) : (
            <div className='flex justify-end gap-2.5 mt-5'>
              <LikeColorIcon
                onClick={() => {
                  if (touchLike) touchLike();
                }}
                className='cursor-pointer'
                style={{ width: "24px" }}
              />
              <DislikeColorIcon
                onClick={() => {
                  if (toggleEdit) toggleEdit();
                }}
                className='cursor-pointer'
                style={{ width: "24px" }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
